import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import illustrationCaregiver from "@/assets/illustration-caregiver.webp";
import { useUserType } from "@/hooks/useUserType";

const benefits = [
  "Perfil verificado con distinción oficial",
  "Pagos automáticos cada semana",
  "Comunidad de personas que aman a los animales como vos",
  "Soporte del equipo Compaws cuando lo necesités",
];

const BecomeSitterBanner = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { setType } = useUserType();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-16 md:py-20 px-4"
      style={{
        background: `
          radial-gradient(ellipse at 30% 20%, #eef4e9 0%, transparent 50%),
          radial-gradient(ellipse at 70% 80%, #fdf8e3 0%, transparent 50%),
          radial-gradient(ellipse at 50% 50%, #ffffff 0%, transparent 60%),
          linear-gradient(135deg, #eef4e9 0%, #ffffff 50%, #fdf8e3 100%)
        `
      }}
    >
      <div className={`container-custom mx-auto transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* Left column */}
          <div className="order-2 md:order-1">
            <p className="font-poppins text-xl font-semibold tracking-widest uppercase mb-4" style={{ color: '#93cfa6' }}>
              PARA CUIDADORES
            </p>
            <h2 className="text-2xl md:text-5xl lg:text-6xl font-fredoka font-bold mb-6 leading-tight" style={{ color: '#252a2a' }}>
              ¿Amás a los animales?<br className="hidden md:block" />
              Conviértelo en tu trabajo.
            </h2>
            <p className="font-poppins text-base md:text-2xl mb-8" style={{ color: '#252a2a', lineHeight: 1.4 }}>
              Conviértete en Compawdre.{" "}
              <br />Tú decides tu horario, tus servicios y tus tarifas.
            </p>

            <div className="space-y-4 mb-10">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 min-w-[24px] min-h-[24px] rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#93cfa6' }}>
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="font-poppins text-lg" style={{ color: '#252a2a', lineHeight: 1.3 }}>{b}</span>
                </div>
              ))}
            </div>

            <div className="text-center md:text-left">
              <Link
                to="/registro"
                onClick={() => setType("compawdre")}
                className="inline-block font-fredoka font-semibold px-8 py-4 rounded-full transition-all duration-200 text-lg"
                style={{
                  backgroundColor: '#eef4e9',
                  color: '#252a2a',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#93cfa6';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#eef4e9';
                }}
              >
                Quiero ser Compawdre
              </Link>
              <p className="font-poppins text-sm mt-4 text-center md:text-left" style={{ color: '#252a2a' }}>
                Es gratis registrarse. Sin cuota mensual.
              </p>
            </div>
          </div>

          {/* Right column */}
          <div className="flex justify-center order-1 md:order-2">
            <img
              src={illustrationCaregiver}
              alt="Cuidadora con mascota"
              width={380}
              height={380}
              loading="lazy"
              className="w-48 md:w-72 lg:w-[380px] object-contain"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BecomeSitterBanner;
